import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { DocumentList, DownloadDocumentsList } from '../../components';
import {
  getSignUrl, getOnboardingDocs, toggleDrawer,
  GET_ONBOARDING_DOCS, LOADING_PROP_TYPE, setSelectedDocument,
  getOnboardingDocumentDownloadUrl, getOnboardingDocumentPreviewUrl,
} from '../../modules';
import './OnboardingDocuments.scss';
import { downloadFromUrl, convertBase64ToBlobUrl } from '../../helpers';

class OnboardingDocuments extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      target: document.getElementById('onboardingdocuments-ui'),
      onboardingDocs: [],
      fileDownload: [],
      currentDocDownloadId: null,
      determineSignatureError: false,
    };
  }

  componentDidMount() {
    const { target } = this.state;
    if (target) {
      this.props.getOnboardingDocs();
      // when gremlins says we need to run form validation, this sets a flag that gets sent to DocumentList to check for unsigned docs
      document.removeEventListener('validateEsignatures', this.determineSignatureErrorStatus, false);
      document.addEventListener('validateEsignatures', this.determineSignatureErrorStatus, false);
    }
  }

  componentDidUpdate(prevProps) {
    const { signatureRequest: { onboardingDocs }, loading } = this.props;
    if (onboardingDocs && prevProps.signatureRequest.onboardingDocs !== onboardingDocs) {
      this.addDataToState('onboardingDocs', onboardingDocs);
      // Trigger an event to send the total number of onboarding documents to ADHR 2.0
      const esignatureDocs = onboardingDocs.filter(doc => doc.type === 'SignatureRequest');
      console.log(esignatureDocs);
      const docCount = new CustomEvent('onboardingDocCount', { detail: { docCount: esignatureDocs.length } });
      document.dispatchEvent(docCount);
      onboardingDocs.forEach((doc) => {
        if (doc.type === 'SignatureRequest') {
          const onboardingSignature = doc.isHelloSignDocument
            ? doc.signatures.filter(signature => signature.signer.role === 'OnboardingSignatory')[0]
            : doc;
          if (onboardingSignature && onboardingSignature.status === 'Complete') {
            const signedDocs = new Set(window.asure.appSettings.esignature.onboarding.signedDocs);
            signedDocs.add(onboardingSignature.id);
            window.asure.appSettings.esignature.onboarding.signedDocs = [...signedDocs];
            window.document.dispatchEvent(new Event('onboardingDocSigned'));
          }
        }
      });
    }
    // We need to tell ADHR 2.0 that there aren't any docs so that it can hide the e-signature ui
    if (loading[GET_ONBOARDING_DOCS] === 'complete' && onboardingDocs.length === 0) {
      const docCount = new CustomEvent('onboardingDocCount', { detail: { docCount: 0 } });
      document.dispatchEvent(docCount);
    }
    const { fileDownload } = this.props;
    if (this.state.target &&
      ((!prevProps.fileDownload && fileDownload) ||
      (prevProps.fileDownload && prevProps.fileDownload.url !== fileDownload.url))
    ) {
      const { url, fileName } = fileDownload;
      const fileUrl = url && url.split(',')[0].split(';').pop() === 'base64' ? convertBase64ToBlobUrl(url) : url;
      downloadFromUrl(fileUrl, fileName);
    }
  }

  addDataToState(property, data) {
    this.setState({ [property]: data });
  }

  signDocument(document) {
    if (document.isHelloSignDocument) {
      this.props.getSignUrl(document.id);
    } else {
      this.props.setSelectedDocument(document);
      this.props.getOnboardingDocumentPreviewUrl(document.id);
    }
    this.props.toggleDrawer(true);
  }

  handleDownload = (documentId) => {
    const { filename } = this.props.signatureRequest.onboardingDocs.filter(doc => doc.id === documentId)[0];
    this.props.getOnboardingDocumentDownloadUrl(documentId, filename);
    const { state } = this;
    this.setState({
      ...state,
      currentDocDownloadId: documentId,
    });
  };

  determineSignatureErrorStatus = () => {
    this.setState({ determineSignatureError: true });
  }

  render() {
    const { target, onboardingDocs, fileDownload } = this.state;
    const { loading } = this.props;
    const componentContent = (
      <div>
        <div className="onboarding-document-list">
          <DocumentList
            documents={onboardingDocs}
            title="REVIEW_AND_ESIGN_DOCUMENTS"
            onSign={this.signDocument.bind(this)}
            isLoading={loading[GET_ONBOARDING_DOCS] === 'loading'}
            determineSignatureError={this.state.determineSignatureError}
          />
        </div>

        <div className="onboarding-download-document-list">
          <DownloadDocumentsList
            handleDownload={this.handleDownload}
            fileDownload={fileDownload}
            documents={onboardingDocs}
            title="DOC_FOR_YOU_TO_DOWNLOAD"
            isLoading={loading[GET_ONBOARDING_DOCS] === 'loading'}
          />
        </div>
      </div>
    );
    if (target) {
      return createPortal(componentContent, target);
    }
    return null;
  }
}

OnboardingDocuments.defaultProps = {
  fileDownload: {},
};

OnboardingDocuments.propTypes = {
  toggleDrawer: PropTypes.func.isRequired,
  signatureRequest: PropTypes.shape({
    onboardingDocs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.oneOf(['Pending', 'Complete', 'Declined', 'Unknown']).isRequired,
      filename: PropTypes.string,
      signatures: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        status: PropTypes.oneOf(['Pending', 'Complete', 'Declined', 'Unknown']),
        signer: PropTypes.shape({
          emailAddress: PropTypes.string,
          name: PropTypes.string,
          role: PropTypes.string,
        }).isRequired,
      })),
    })),
  }).isRequired,
  fileDownload: PropTypes.shape({
    url: PropTypes.string,
    filename: PropTypes.string,
  }),
  getSignUrl: PropTypes.func.isRequired,
  getOnboardingDocs: PropTypes.func.isRequired,
  loading: LOADING_PROP_TYPE.isRequired,
  getOnboardingDocumentDownloadUrl: PropTypes.func.isRequired,
  getOnboardingDocumentPreviewUrl: PropTypes.func.isRequired,
  setSelectedDocument: PropTypes.func.isRequired,
};

export default connect(
  state => ({
    loading: state.appState.loading,
    docs: state.resources.docs,
    signatureRequest: state.resources.signatureRequest,
    fileDownload: state.resources.docs.fileDownload,
  }),
  dispatch => ({
    getSignUrl: requestId => dispatch(getSignUrl(requestId)),
    getOnboardingDocs: () => dispatch(getOnboardingDocs()),
    getOnboardingDocumentDownloadUrl: (documentId, fileName) => {
      dispatch(getOnboardingDocumentDownloadUrl(documentId, fileName));
    },
    getOnboardingDocumentPreviewUrl: (documentId) => {
      dispatch(getOnboardingDocumentPreviewUrl(documentId));
    },
    toggleDrawer: (open) => {
      dispatch(toggleDrawer(open));
    },
    setSelectedDocument: (document) => {
      dispatch(setSelectedDocument(document));
    },
  })
)(OnboardingDocuments);



// WEBPACK FOOTER //
// ./src/containers/OnboardingDocuments/OnboardingDocuments.jsx